// Concept2 PM5 BLE telemetry decoder.
//
// The PM5 multiplexes rowing telemetry across several proprietary GATT
// characteristics (base UUID CE060000-43E5-11E4-916C-0800200C9A66). All
// multi-byte fields are little-endian. Offsets follow the Concept2 PM
// Bluetooth Smart Interface Definition.
//
// We decode the three characteristics that carry the metrics Kilo headlines:
//   0x0032 Additional Status 1 -> speed, stroke rate, current pace (/500m), HR
//   0x0033 Additional Status 2 -> average power (watts), total calories
//   0x0035 Stroke Data          -> stroke count, distance

import { PM5_CHARACTERISTICS } from './hardwareCatalog'
import type { TelemetryMetric } from './types'

function uint24LE(view: DataView, offset: number): number {
  return view.getUint8(offset) | (view.getUint8(offset + 1) << 8) | (view.getUint8(offset + 2) << 16)
}

type ParsedMetrics = Partial<Record<TelemetryMetric, number>>

/** Normalise a 128-bit or 16-bit characteristic UUID to lowercase string form. */
export function normalizeUuid(uuid: number | string): string {
  if (typeof uuid === 'number') return uuid.toString(16).padStart(4, '0')
  return uuid.toLowerCase()
}

/**
 * Decode a raw PM5 characteristic payload into normalized telemetry metrics.
 * Unknown characteristics or short buffers yield an empty object — never throws.
 */
export function parsePm5Characteristic(uuid: number | string, value: DataView): ParsedMetrics {
  const id = normalizeUuid(uuid)

  try {
    if (id === PM5_CHARACTERISTICS.additionalStatus1 && value.byteLength >= 9) {
      const elapsed = uint24LE(value, 0) / 100 // 0.01 s units
      const speed = value.getUint16(3, true) / 1000 // 0.001 m/s
      const strokeRate = value.getUint8(5) // strokes/min
      const hr = value.getUint8(6) // bpm (255 = invalid)
      const currentPace = value.getUint16(7, true) / 100 // 0.01 s per 500m

      const metrics: ParsedMetrics = {
        elapsed_s: elapsed,
        mean_velocity_ms: speed,
        stroke_rate: strokeRate,
        split_500m_sec: currentPace,
      }
      if (hr > 0 && hr < 255) metrics.heart_rate_bpm = hr
      return metrics
    }

    if (id === PM5_CHARACTERISTICS.additionalStatus2 && value.byteLength >= 8) {
      const elapsed = uint24LE(value, 0) / 100
      const avgPower = value.getUint16(4, true) // watts
      const calories = value.getUint16(6, true) // kcal
      return {
        elapsed_s: elapsed,
        power_watts: avgPower,
        calories_kcal: calories,
      }
    }

    if (id === PM5_CHARACTERISTICS.generalStatus && value.byteLength >= 6) {
      const elapsed = uint24LE(value, 0) / 100
      const distance = uint24LE(value, 3) / 10 // 0.1 m
      return { elapsed_s: elapsed, distance_m: distance }
    }

    if (id === PM5_CHARACTERISTICS.strokeData && value.byteLength >= 19) {
      const distance = uint24LE(value, 3) / 10
      const strokeCount = value.getUint16(17, true)
      return { distance_m: distance, rep_count: strokeCount }
    }
  } catch {
    // Malformed frame — drop it rather than corrupting the live stream.
    return {}
  }

  return {}
}

/** Format a PM5 split pace (seconds per 500m) as M:SS.s. */
export function formatSplit(secondsPer500m: number): string {
  if (!Number.isFinite(secondsPer500m) || secondsPer500m <= 0) return '--:--'
  const minutes = Math.floor(secondsPer500m / 60)
  const seconds = secondsPer500m - minutes * 60
  return `${minutes}:${seconds.toFixed(1).padStart(4, '0')}`
}
